<template>
  <button
    class="st-swatch"
    :class="[`st-swatch--${size}`, `st-swatch--${shape}`, { 'is-selected': selected }]"
    type="button"
    :title="label"
    :aria-label="label"
    :aria-pressed="selected"
  >
    <span class="st-swatch__fill" :style="fillStyle">
      <font-awesome-icon v-if="icon" :icon="['fas', icon]" />
      <slot />
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    /** Fill color (any CSS color). Omit for an icon/glyph-only swatch. */
    color?: string;
    textColor?: string;
    borderColor?: string;
    icon?: string;
    label: string;
    selected?: boolean;
    size?: 'sm' | 'md';
    shape?: 'circle' | 'square';
  }>(),
  { size: 'md', shape: 'circle' },
);

const fillStyle = computed(() => ({
  ...(props.color ? { background: props.color } : {}),
  ...(props.textColor ? { color: props.textColor } : {}),
  ...(props.borderColor ? { boxShadow: `inset 0 0 0 1px ${props.borderColor}` } : undefined),
}));
</script>

<style scoped>
.st-swatch {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: 2px solid transparent;
  border-radius: var(--st-radius-full);
  background: transparent;
  cursor: pointer;
  transition: var(--st-transition);
}
.st-swatch:hover {
  border-color: var(--st-border-strong);
}
.st-swatch.is-selected {
  border-color: var(--st-accent);
}
.st-swatch__fill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: var(--st-radius-full);
  background: var(--st-surface-sunken);
  color: var(--st-text);
  font-size: var(--st-text-xs);
  box-shadow: inset 0 0 0 1px rgba(15, 17, 21, 0.12);
}
.st-swatch--square,
.st-swatch--square .st-swatch__fill {
  border-radius: var(--st-radius-sm);
}
.st-swatch--sm .st-swatch__fill {
  width: 18px;
  height: 18px;
}
</style>
